import { useCallback, useEffect, useState } from 'react'
import { coursesAPI } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import type { Course, CourseEnrollment, CourseModule, Lesson } from '../../types'

export interface ModuleWithLessons extends CourseModule {
  lessons: Lesson[]
}

export type CourseState =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'error' }
  | {
      kind: 'ready'
      course: Course
      modules: ModuleWithLessons[]
      /** The viewer's enrolment, when they have one that's active or completed. */
      enrollment: CourseEnrollment | null
      /** Lesson ids the viewer has completed. */
      completed: Set<number>
      /** The viewer is the course's instructor (or an admin). */
      manages: boolean
    }

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status

const byOrder = <T extends { display_order: number; id: number }>(a: T, b: T) =>
  a.display_order - b.display_order || a.id - b.id

/** The course at /learn/<slug> as the viewer may see it, its outline, and their enrolment and progress. */
export const useCourse = (slug: string | undefined) => {
  const user = useAuthStore((state) => state.user)
  const signedIn = useAuthStore((state) => Boolean(state.token))
  const [state, setState] = useState<CourseState>({ kind: 'loading' })

  const load = useCallback(async () => {
    if (!slug) return setState({ kind: 'missing' })
    try {
      const course = await coursesAPI.bySlug(slug)
      const modules = (await coursesAPI.modules(course.id)).sort(byOrder)
      const withLessons = await Promise.all(
        modules.map(async (module) => ({ ...module, lessons: (await coursesAPI.lessons(module.id)).sort(byOrder) }))
      )
      let enrollment: CourseEnrollment | null = null
      let completed = new Set<number>()
      if (signedIn) {
        const mine = await coursesAPI.myEnrollments().catch(() => [] as CourseEnrollment[])
        enrollment =
          mine.find((e) => e.course_id === course.id && (e.status === 'active' || e.status === 'completed')) || null
        if (enrollment) {
          const progress = await coursesAPI.progress(enrollment.id).catch(() => [])
          completed = new Set(progress.filter((p) => p.is_completed).map((p) => p.lesson_id))
        }
      }
      const manages = Boolean(user && (user.id === course.instructor_id || user.is_superuser))
      setState({ kind: 'ready', course, modules: withLessons, enrollment, completed, manages })
    } catch (error) {
      setState({ kind: statusOf(error) === 404 ? 'missing' : 'error' })
    }
  }, [slug, signedIn, user])

  useEffect(() => {
    setState({ kind: 'loading' })
    load()
  }, [load])

  return { state, reload: load, setState }
}

/** The course's published lessons in reading order. */
export const lessonsInOrder = (modules: ModuleWithLessons[]): Lesson[] => modules.flatMap((m) => m.lessons)
